// -----------------------------------------------------------------------------
// Carrier Home reuses <TabsHomePage />
// 1) Map carrier permissions into the same "roles" shape that <HomePage /> uses.
// 2) After login, if there is a pending invite (carrier_invite_pending),
//      redirect from "/carrier/home" to CarrierInviteViewBid using the correct
//      query string.
// -----------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TabsHomePage from '../../components/tabsHomePage/tabsHomePage';
import {
  ModuleEnumReverse,
  ModuleActionEnumReverse,
} from 'src/M_Components/M_Utils/permissions';
import { STORAGE_CARRIER } from 'src/components/auth/storageKeys';

/**
 * Storage key used to hold the normalized "pending invite" context.
 * This is created by CarrierInviteEntryPage and consumed after login.
 *
 * Example shape:
 * {
 *   type: "spot" | "phase",
 *   tenderId: number | null,
 *   tenderPhaseId: number | null,
 *   token: string,
 *   createdAt: number
 * }
 */
const PENDING_INVITE_KEY = 'carrier_invite_pending';

/**
 * Internal route where the invite/bid wrapper page
 * (CarrierInviteViewBidPage) lives inside the carrier realm.
 *
 * This must stay in sync with:
 *  - CarrierInviteEntryPage (INVITE_TARGET_ROUTE)
 *  - Menu URLs in the carrier realm
 */
const INVITE_TARGET_ROUTE = '/carrier/home/CarrierInviteViewBid';

/**
 * Builds the "roles" array based on the carrier permissions.
 * This produces the same [{ Value: "ModuleAction" }] structure used by HomePage.
 *
 * Rules:
 * - When action == Admin, expand into Create/Update/Delete/Read/List.
 * - Remove duplicates to keep the array clean.
 */
function buildRolesFromCarrierPermissions() {
  try {
    const raw = localStorage.getItem(STORAGE_CARRIER.permissions);
    const tempPermissions = raw ? JSON.parse(raw) : [];
    const bag = new Set();

    tempPermissions.forEach((p) => {
      const moduleName = ModuleEnumReverse?.[p?.Module];
      const actionName = ModuleActionEnumReverse?.[p?.Action];
      if (!moduleName || !actionName) return;

      if (actionName === 'Admin') {
        ['Create', 'Update', 'Delete', 'Read', 'List'].forEach((a) => {
          bag.add(`${moduleName}${a}`);
        });
      } else {
        bag.add(`${moduleName}${actionName}`);
      }
    });

    return Array.from(bag).map((v) => ({ Value: v }));
  } catch {
    return [];
  }
}

export default function CarrierHomePage() {
  const [roles, setRoles] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  // ---------------------------------------------------------------------------
  // 1) Build roles from carrier permissions (once on mount).
  //    Menus do carrier passam a ser geridos dentro de <TabsHomePage />.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setRoles(buildRolesFromCarrierPermissions());
  }, []);

  // ---------------------------------------------------------------------------
  // 2) Pending invite handling
  //
  // Flow:
  //  - CarrierInviteEntryPage stores carrier_invite_pending.
  //  - If the carrier is not authenticated, the user goes to "/carrier/login".
  //  - After login, the Login page redirects to "#/carrier/home".
  //  - Once we land here, if there is a valid pending invite:
  //       * read it
  //       * remove it
  //       * navigate to "/carrier/home/CarrierInviteViewBid?...".
  //
  // This ensures the invite flow continues automatically after login.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Only consider pending invites inside the carrier home realm.
    // We do NOT require being exactly at "/carrier/home" because TabsHomePage
    // or persisted navigation may land the user on a nested route first.
    const isInsideCarrierHome = location.pathname?.startsWith('/carrier/home');
    if (!isInsideCarrierHome) return;

    // Avoid redirect loops if we are already at the invite wrapper route.
    const isAlreadyOnInviteWrapper =
      location.pathname === INVITE_TARGET_ROUTE ||
      location.pathname?.startsWith(`${INVITE_TARGET_ROUTE}/`);
    if (isAlreadyOnInviteWrapper) return;

    try {
      const raw = localStorage.getItem(PENDING_INVITE_KEY);
      if (!raw) return;

      const ctxInvite = JSON.parse(raw);

      const isValid =
        ctxInvite &&
        typeof ctxInvite.token === 'string' &&
        ctxInvite.token.length > 0 &&
        (ctxInvite.tenderId || ctxInvite.tenderPhaseId);

      // If invalid, remove it to avoid repeatedly parsing broken data.
      if (!isValid) {
        localStorage.removeItem(PENDING_INVITE_KEY);
        return;
      }

      const searchParams = new URLSearchParams();
      if (ctxInvite.type) searchParams.set('type', String(ctxInvite.type));
      if (ctxInvite.tenderId)
        searchParams.set('tenderId', String(ctxInvite.tenderId));
      if (ctxInvite.tenderPhaseId)
        searchParams.set('tenderPhaseId', String(ctxInvite.tenderPhaseId));
      searchParams.set('token', String(ctxInvite.token));

      // Prevent repeated redirections
      localStorage.removeItem(PENDING_INVITE_KEY);

      navigate(`${INVITE_TARGET_ROUTE}?${searchParams.toString()}`, {
        replace: true,
      });
    } catch (e) {
      // If storage is malformed, remove it to avoid repeated failures.
      try {
        localStorage.removeItem(PENDING_INVITE_KEY);
      } catch { }
    }
  }, [location.pathname, navigate]);

  return <TabsHomePage roles={roles} />;
}
